import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { useResetPassword } from '@/features/auth';
import { Button } from '@/shared/ui/Button';
import { createPasswordSchema } from '@/shared/lib/passwordValidation';
import { AuthInput } from './ui/AuthInput';
import { AuthLayout } from './ui/AuthLayout';
import { authFormClassName, authMessageClassName, authServerErrorClassName } from './ui/authForm';

// ResetPassword на новом стеке. Прямого 1:1 референса у фронтендера нет.
// Делаем по образцу Login/RequestPasswordReset: двухколоночный layout с
// AuthHeader/AuthInput/Picture, react-hook-form + zod + useResetPassword.
// token и hash берутся из ?token=&hash= в URL — они приходят из письма
// после requestResetPassword.

const createResetSchema = (t: TFunction<'auth'>) =>
  z
    .object({
      password: createPasswordSchema(t),
      confirm_password: z.string().min(1, t('validation.passwordConfirmRequired')),
    })
    .refine((data) => data.password === data.confirm_password, {
      message: t('validation.passwordMismatch'),
      path: ['confirm_password'],
    });

type ResetFormData = z.infer<ReturnType<typeof createResetSchema>>;

export function ResetPassword() {
  const { t } = useTranslation(['auth', 'common']);
  const resetSchema = useMemo(() => createResetSchema(t), [t]);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const hash = params.get('hash') ?? '';
  const resetMutation = useResetPassword();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    setError: setFormError,
    clearErrors,
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: { password: '', confirm_password: '' },
  });

  const handleInputChange = (field: keyof ResetFormData) => {
    if (errors.root?.server) clearErrors('root.server');
    if (errors[field]) clearErrors(field);
  };

  const getShowError = (field: keyof ResetFormData) =>
    !!errors[field] || !!errors.root?.server;

  // Если token/hash отсутствуют — нечего сбрасывать.
  if (!token || !hash) {
    return (
      <AuthLayout
        title={t('reset.invalidLinkTitle')}
        questionText=""
        linkTo="/login"
        linkText={t('signIn')}
        hint={t('reset.invalidLinkText')}
      >
        <div className="mt-8 w-full">
          <Button
            type="button"
            className="min-h-[48px] w-full !px-0"
            onClick={() => navigate('/forgot-password')}
          >
            {t('reset.requestAgain')}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  const onSubmit = async (data: ResetFormData) => {
    clearErrors('root.server');
    try {
      const outcome = await resetMutation.mutateAsync({
        token,
        hash,
        data: {
          password: data.password,
          confirm_password: data.confirm_password,
        },
      });
      switch (outcome.kind) {
        case 'ok':
          return;
        case 'validation':
          if (outcome.errors.password?.[0]) {
            setFormError('password', {
              type: 'server',
              message: outcome.errors.password[0],
            });
          }
          if (outcome.errors.confirm_password?.[0]) {
            setFormError('confirm_password', {
              type: 'server',
              message: outcome.errors.confirm_password[0],
            });
          }
          if (Object.keys(outcome.errors).length === 0) {
            setFormError('root.server', {
              type: 'manual',
              message: t('reset.failed'),
            });
          }
          break;
        case 'error':
          setFormError('root.server', {
            type: 'manual',
            message: t('reset.failedLater'),
          });
          break;
      }
    } catch {
      setFormError('root.server', {
        type: 'manual',
        message: t('common:networkError'),
      });
    }
  };

  const showSuccess =
    isSubmitSuccessful && !errors.root?.server && !errors.password && !errors.confirm_password;

  if (showSuccess) {
    return (
      <AuthLayout
        title={t('reset.doneTitle')}
        questionText={t('rememberedOldPassword')}
        linkTo="/login"
        linkText={t('signIn')}
      >
        <p className={authMessageClassName()}>{t('reset.doneText')}</p>
        <div className="mt-8 w-full">
          <Button
            type="button"
            className="min-h-[48px] w-full !px-0"
            onClick={() => navigate('/login')}
          >
            {t('signIn')}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('reset.title')}
      questionText={t('rememberedOldPassword')}
      linkTo="/login"
      linkText={t('signIn')}
      hint={t('reset.hint')}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className={authFormClassName()}
      >
        {errors.root?.server && (
          <div className={authServerErrorClassName()}>
            {errors.root.server.message}
          </div>
        )}

        <div className="relative w-full">
          <AuthInput
            type="password"
            autoComplete="new-password"
            label={t('fields.newPassword')}
            example={t('examples.newPassword')}
            error={errors.password?.message}
            showError={getShowError('password')}
            {...register('password', { onChange: () => handleInputChange('password') })}
          />
        </div>

        <div className="relative w-full">
          <AuthInput
            type="password"
            autoComplete="new-password"
            label={t('fields.repeatPassword')}
            example={t('examples.repeatPassword')}
            error={errors.confirm_password?.message}
            showError={getShowError('confirm_password')}
            {...register('confirm_password', {
              onChange: () => handleInputChange('confirm_password'),
            })}
          />
        </div>

        <div className="mt-[6px]">
          <Button
            type="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            className="min-h-[48px] w-full !px-0"
          >
            {t('reset.submit')}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
